'use client';
import styles from './ymm.module.scss';
import { getCapabilities, getMakesForYear, getModelsForMake, getYMMCookies, getYearsFrom } from '@/packages/sxm/ymm';
import { useCallback, useEffect, useState } from 'react';
import { Capabilities, Model } from '@/packages/sxm/ymm/types';
import type { Props } from './interfaces';
import { Select, Option, OptionGroup } from '@/ui-components/atoms/Inputs/Select';

export function YMM(props: Props): React.JSX.Element {
    const { onCarReady, reset: resetYMM } = props;
    const [years, setYears] = useState<string[]>([]);
    const [makes, setMakes] = useState<string[]>([]);
    const [models, setModels] = useState<Model[]>([]);
    const [year, setYear] = useState('');
    const [make, setMake] = useState('');
    const [model, setModel] = useState('');
    const [id, setId] = useState('');
    const [capabilities, setCapabilities] = useState<Capabilities>();

    const selectTheme = props?.selectTheme ? props.selectTheme : 'outline';

    const getYears = useCallback(async () => {
        if (years.length) {
            return;
        }

        const yearList = await getYearsFrom('2004');
        setYears(yearList);
    }, [years]);

    const getMakes = useCallback(async (customYear: string) => {
        const makesList = await getMakesForYear(customYear);
        setMakes(makesList);
    }, []);

    const getModels = useCallback(async (customYear: string, customMake: string) => {
        const modelsList = await getModelsForMake(customYear, customMake);
        setModels(modelsList);
    }, []);

    useEffect(() => {
        async function reset() {
            const ymmCookie = getYMMCookies().sxm_radio.split('|');

            if (resetYMM) {
                await getYears();
                if (ymmCookie[1]) {
                    await getMakes(ymmCookie[1]);
                    await getModels(ymmCookie[1], ymmCookie[2]);

                    setYear(ymmCookie[1]);
                    setMake(ymmCookie[2]);
                    setModel(ymmCookie[3]);
                    setId(ymmCookie[4]);
                }
            }
        }

        reset();
    }, [getMakes, getModels, getYears, resetYMM]);

    useEffect(() => {
        async function init() {
            const ymmCookie = getYMMCookies().sxm_radio.split('|');

            if (ymmCookie[1]) {
                setYear(ymmCookie[1]);
                setMake(ymmCookie[2]);
                setModel(ymmCookie[3]);
                setId(ymmCookie[4]);
            }
        }
        init();
    }, []);

    useEffect(() => {
        const getCarCapabilities = async (customId: string) => {
            const capabilities = await getCapabilities(customId);
            setCapabilities(capabilities);
        };

        if (id) {
            getCarCapabilities(id);
        }
    }, [id]);

    useEffect(() => {
        onCarReady({
            year: year,
            make: make,
            model: model,
            id: id,
            capabilities: capabilities,
        });
    }, [year, make, model, id, capabilities, onCarReady]);

    function yearHandler(element: HTMLInputElement | undefined, params: { value: string }) {
        if (params.value && year !== params.value) {
            setYear(params.value);
            setMake('');
            setModel('');
            setId('');
            setMakes([]);
            setModels([]);
            getMakes(params.value);
        }
    }

    function makeHandler(element: HTMLInputElement | undefined, params: { value: string }) {
        if (params.value && make !== params.value) {
            setMake(params.value);
            setModel('');
            setId('');
            setModels([]);
            getModels(year, params.value);
        }
    }

    function modelHandler(element: HTMLInputElement | undefined, params: { value: string; data?: Model }) {
        if (params.value && params.data && model !== params.value) {
            setCapabilities(undefined);
            setModel(params.value);
            setId(params.data.ymmComboId);
        }
    }

    return (
        <div className={styles.bar + ' ' + styles[props.theme]}>
            <div className={styles.year}>
                <Select
                    label="Year"
                    type="form"
                    theme={selectTheme}
                    name="year"
                    disabled={years.length === 0}
                    onChange={yearHandler}
                    value={year}
                    placeholder={year}
                    dataCy="molecules-ymm-form-year-select"
                >
                    <OptionGroup>
                        {years
                            .sort()
                            .reverse()
                            .map((year, i) => (
                                <Option key={i} value={`${year}`}>{`${year}`}</Option>
                            ))}
                    </OptionGroup>
                </Select>
            </div>
            <div className={styles.make}>
                <Select
                    label="Make"
                    type="form"
                    theme={selectTheme}
                    name="make"
                    disabled={makes.length === 0}
                    onChange={makeHandler}
                    value={make}
                    placeholder={make}
                    dataCy="molecules-ymm-form-make-select"
                >
                    <OptionGroup>
                        {makes.map((make, i) => (
                            <Option key={i} value={make}>
                                {make}
                            </Option>
                        ))}
                    </OptionGroup>
                </Select>
            </div>
            <div className={styles.model}>
                <Select<Model>
                    label="Model"
                    type="form"
                    theme={selectTheme}
                    name="model"
                    disabled={models.length === 0}
                    onChange={modelHandler}
                    value={model}
                    placeholder={model}
                    dataCy="molecules-ymm-form-model-select"
                >
                    <OptionGroup>
                        {models.map((model, i) => (
                            <Option key={i} value={model.vehicleModel} object={model}>
                                {model.vehicleModel}
                            </Option>
                        ))}
                    </OptionGroup>
                </Select>
            </div>
        </div>
    );
}
